import { useEffect, useState } from 'react';
import { Alert, Box, Button } from '@backstage/ui';
import { useApi } from '@backstage/frontend-plugin-api';
import { googleAuthApiRef } from '@backstage/core-plugin-api';
import { useTemplateSecrets } from '@backstage/plugin-scaffolder-react';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

/**
 * The secret name the Catalog's `control:*` scaffolder actions read.
 *
 * Must match CONTROL_ID_TOKEN_SECRET in
 * packages/backend/src/modules/scaffolderControl/index.ts. Control acts as the
 * requesting human, so a task with no caller identity cannot proceed.
 */
export const CONTROL_ID_TOKEN_SECRET = 'CONTROL_ID_TOKEN';

/**
 * Collects the signed-in user's Google ID token into the scaffolder's secrets
 * channel. Renders nothing.
 *
 * # Why this exists separately from PlacementPicker
 *
 * PlacementPicker used to be the ONLY thing that set this secret, as a side
 * effect of resolving placement. That coupled "I need the caller's identity" to
 * "I need launchpad to choose an environment", and two of the three golden paths
 * need the first without the second — `answerrocket-environment` (deleted in
 * #76) and `answerrocket-graphrag-engagement` both call
 * `control:rbac:preflight` and neither has a placement step.
 *
 * The result was that both templates failed on step 0, every time, with an error
 * written for whoever wrote the template rather than the person running it:
 *
 *   this template must collect the CONTROL_ID_TOKEN secret — add the
 *   PlacementPicker field, which sets it.
 *
 * A user filled in the whole form before finding out. Identity collection is its
 * own concern, so it is now its own field: add `ui:field: ControlIdToken` to any
 * template that calls a `control:*` action and does not already use
 * PlacementPicker.
 *
 * Setting the same secret twice is harmless — `setSecrets` merges — so a
 * template carrying both fields still works.
 */
export const ControlIdToken = (props: FieldExtensionComponentProps<string>) => {
  const googleAuth = useApi(googleAuthApiRef);
  const { setSecrets } = useTemplateSecrets();
  const [error, setError] = useState<string>();

  const { onChange } = props;

  /**
   * Collect the token, and treat an EMPTY one as a failure.
   *
   * This used to catch only a throw. `getIdToken()` does not throw when the
   * session exists but carries no id_token — it resolves to `''` — so the old
   * version stored an empty secret and then called `onChange` to mark the field
   * SATISFIED. The form said identity was collected, and the task died on its
   * first step with `this template must collect the CONTROL_ID_TOKEN secret`:
   * an error addressed to whoever wrote the template, raised after the user had
   * filled in the whole form. Which is the exact failure this component was
   * written to prevent.
   *
   * Returns whether it worked, so the retry can tell.
   */
  const collect = async (): Promise<boolean> => {
    try {
      const idToken = await googleAuth.getIdToken();
      if (!idToken) {
        setError(
          'Your session did not include a Google ID token. Control acts as the ' +
            'person asking, so the request cannot be made without one.',
        );
        return false;
      }
      setSecrets({ [CONTROL_ID_TOKEN_SECRET]: idToken });
      setError(undefined);
      // Only NOW is the field satisfied. A template may mark it `required` and
      // rely on the form blocking until identity is genuinely in hand.
      onChange(CONTROL_ID_TOKEN_SECRET);
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      return false;
    }
  };

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (!cancelled) await collect();
    })();
    return () => {
      cancelled = true;
    };
    // Once per mount: the token is read at submit time from the secrets
    // channel, and re-running on every render would spam the auth API.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Invisible on success. On failure it must say something AND offer a way
  // out, or the form blocks on a required field with no explanation and no
  // control to interact with.
  //
  // The retry matters more than it looks: a popup opened from a click is
  // permitted where one opened from a mount effect is blocked, so pressing the
  // button can succeed exactly where the automatic attempt could not.
  if (!error) {
    return null;
  }
  return (
    <Box role="alert">
      <Alert
        status="warning"
        title="Could not read your Google identity"
        description={`${error} If this keeps happening, sign out and back in.`}
        customActions={
          <Button size="small" onPress={() => void collect()}>
            Connect Google identity
          </Button>
        }
      />
    </Box>
  );
};
